// src/encounter/services/use-bundles.ts
// A/P BUNDLES — the provider's saved A&P templates (PROD-1839).
//   GET /api/Patients/Bundle/{providerId} → BundleDataModel[]
//
// Flutter: `getBundles` (encounter_data_source_impl.dart:1329-1336) hits
// `${patientAPI(PatientService.Patients)}/Bundle/$providerId`, i.e. `/api/Patients/Bundle/{id}`,
// and maps the raw array with `BundleDataModel.fromJson` (bundle_data_modle.dart). There is no
// paging, no query string and no filter — the whole list comes back and the rail filters it
// client-side by name.
//
// ⚠ WHICH ID. `providerId` here is the LOGGED-IN USER's id, NOT `practitionerId`. The A/P search
// rail's own `providerId` prop resolves to `user.practitionerId ?? user.id` (a different identity
// for a different purpose — it scopes order/favourite searches), and passing that here returns an
// empty list for a provider who demonstrably has bundles in Flutter. The reason is in the Flutter
// source: `getBundle()` (assessment_plan_search_list_widget.dart:1264-1284) calls the bare
// `providerId()`, whose default `forceProviderIdOnly: false` returns `LoginUserData.id`
// (utils.dart:1232-1249) — never `practitionerId`. `user.id` is that field's React analogue.
// Same reasoning, same citation as the Malaffi SSO `providerId` in encounter-shell-page.tsx:247-254.
import { useQuery } from '@tanstack/react-query';
import { get, ApiError } from '@/shared/api/api-client';
import type { PatientAssessment } from '@/types/assessment-api';

/**
 * A saved A&P template. `bundleDetails` is a full `PatientAssessment` — the same shape the
 * encounter's own assessment uses — so importing one is `patientAssessmentToCanvas` over it.
 * Mirrors Flutter's `BundleDataModel` field for field.
 */
export interface ApBundle {
  id?: string | null;
  bundleName?: string | null;
  organizationId?: string | null;
  providerId: string;
  createdDate?: string | null;
  createdBy?: string | null;
  updatedDate?: string | null;
  updatedBy?: string | null;
  isDeleted?: boolean | null;
  bundleDetails: PatientAssessment;
}

/**
 * The provider's saved bundles. Disabled until `providerId` is known — building the URL with an
 * "undefined" segment would 404 against a real route and read as "no bundles" rather than "not
 * asked yet".
 *
 * A 404 resolves to `[]` (mirrors use-assessment.ts): a provider who has never saved a bundle is
 * an empty list, not an error worth surfacing in the rail.
 */
export function useBundles(providerId: string | null | undefined) {
  return useQuery<ApBundle[]>({
    queryKey: ['ap-bundles', providerId],
    enabled: !!providerId,
    queryFn: async ({ signal }) => {
      if (!providerId) return [];
      try {
        const list = await get<ApBundle[]>(
          `/api/Patients/Bundle/${encodeURIComponent(providerId)}`,
          { signal },
        );
        // Tombstoned rows come back on the wire (isDeleted is a real field on the model); the
        // rail must never offer one for import.
        return (list ?? []).filter((b) => !b?.isDeleted);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return [];
        throw e;
      }
    },
  });
}
